/**
 * WB4 Depth — RiskTierPolicy SSOT (action-rule-table `safety{}` blocks).
 */

import type { ObjectRef } from './workbench-focus';
import type { WorkbenchEvidenceStateMetadata } from './workbench-evidence-state';
import {
  buildActionSafety,
  mapWorkbenchSurfacesToSafetySurfaces,
  type RecoverabilityMode,
  type WorkbenchActionSafety,
} from './workbench-action-safety-policy';

export type {
  RecoverabilityMode,
  WorkbenchActionConfirmation,
  WorkbenchActionSafety,
  WorkbenchActionSafetySurface,
} from './workbench-action-safety-policy';
export { buildActionSafety, mapWorkbenchSurfacesToSafetySurfaces };

export interface ResolveActionRiskTierInput {
  actionId: string;
  handlerRef?: string;
  domain?: string;
  businessDomain?: string;
  objectType?: string;
  objectState?: Record<string, unknown>;
  userRole?: string;
}

export interface WorkbenchExecutionRecoverability {
  mode: RecoverabilityMode;
  available: boolean;
  expiresAt?: string;
  revisionId?: string;
  undoSnapshotId?: string;
}

/** Terminal and in-flight phases for kernel dispatch honesty (WB4 Wave 1). */
export type WorkbenchExecutionPhase =
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'requires_approval';

export interface WorkbenchExecutionResult {
  ok: boolean;
  handled: boolean;
  executionPhase: WorkbenchExecutionPhase;
  actionId: string;
  objectRef: ObjectRef;
  riskPolicy: WorkbenchActionSafety;
  timelineEventId?: string;
  evidenceState?: WorkbenchEvidenceStateMetadata;
  recoverability: WorkbenchExecutionRecoverability;
  nextWork?: {
    threadId?: string;
    nextActionId?: string;
    queueId?: string;
  };
  userMessage: {
    title: string;
    description?: string;
  };
}

export function isWorkbenchExecutionPending(result: WorkbenchExecutionResult): boolean {
  return result.executionPhase === 'pending' || result.executionPhase === 'requires_approval';
}

export function isWorkbenchExecutionTerminal(result: WorkbenchExecutionResult): boolean {
  return !isWorkbenchExecutionPending(result);
}

export function isWorkbenchExecutionSucceeded(result: WorkbenchExecutionResult): boolean {
  return result.executionPhase === 'succeeded';
}

function resolveExecutionPhase(input: {
  ok: boolean;
  handled: boolean;
  executionPhase?: WorkbenchExecutionPhase;
  userMessage?: Partial<WorkbenchExecutionResult['userMessage']>;
}): WorkbenchExecutionPhase {
  if (input.executionPhase) {
    return input.executionPhase;
  }
  if (!input.handled) {
    return 'failed';
  }
  if (!input.ok) {
    const title = input.userMessage?.title?.trim() ?? '';
    if (title.includes('취소')) {
      return 'cancelled';
    }
    return 'failed';
  }
  return 'succeeded';
}

import { resolveRecoverabilityFromPolicy } from './workbench-recoverability-ladder';
import {
  collectGoldenPathSafetyGateViolations,
  countActionRuleSafetyEntries,
  listActionRuleSafetyEntries,
  resolveActionSafetyByActionId,
  resolveActionSafetyByHandlerRef,
} from './workbench-action-safety-lookup';

export function allowsToastUndo(safety: WorkbenchActionSafety): boolean {
  if (safety.riskTier === 'finance') {
    return false;
  }
  return safety.recoverabilityMode === 'toast_undo';
}

export function isApprovalCorrectionMode(safety: WorkbenchActionSafety): boolean {
  return safety.recoverabilityMode === 'approval_correction';
}

export function isRecoverabilityReversible(safety: WorkbenchActionSafety): boolean {
  return (
    allowsToastUndo(safety) ||
    safety.recoverabilityMode === 'revision_restore' ||
    safety.recoverabilityMode === 'compensating_transaction'
  );
}

export function requiresConfirmationGate(safety: WorkbenchActionSafety): boolean {
  return safety.confirmation !== 'none';
}

export {
  collectGoldenPathSafetyGateViolations,
  countActionRuleSafetyEntries,
  listActionRuleSafetyEntries,
  resolveActionSafetyByActionId,
  resolveActionSafetyByHandlerRef,
};

export { collectPhaseBSafetyViolations } from './workbench-action-safety-policy';

/** SSOT wrapper — delegates to recoverability ladder + action-rule-table `safety{}`. */
export function resolveActionRiskTier(input: ResolveActionRiskTierInput): WorkbenchActionSafety {
  return resolveRecoverabilityFromPolicy(input.actionId, input.handlerRef);
}

export { resolveRecoverabilityFromPolicy } from './workbench-recoverability-ladder';

export function buildWorkbenchExecutionResult(input: {
  ok: boolean;
  handled: boolean;
  actionId: string;
  objectRef: ObjectRef;
  riskPolicy: WorkbenchActionSafety;
  executionPhase?: WorkbenchExecutionPhase;
  timelineEventId?: string;
  evidenceState?: WorkbenchEvidenceStateMetadata;
  revisionId?: string;
  undoSnapshotId?: string;
  expiresAt?: string;
  nextWork?: WorkbenchExecutionResult['nextWork'];
  userMessage?: Partial<WorkbenchExecutionResult['userMessage']>;
}): WorkbenchExecutionResult {
  const executionPhase = resolveExecutionPhase(input);
  const recoverabilityAvailable =
    executionPhase === 'succeeded' &&
    input.ok &&
    input.handled &&
    (allowsToastUndo(input.riskPolicy) ||
      input.riskPolicy.recoverabilityMode === 'revision_restore' ||
      input.riskPolicy.recoverabilityMode === 'approval_correction' ||
      input.riskPolicy.recoverabilityMode === 'compensating_transaction');

  return {
    ok: input.ok,
    handled: input.handled,
    executionPhase,
    actionId: input.actionId,
    objectRef: input.objectRef,
    riskPolicy: input.riskPolicy,
    ...(input.timelineEventId ? { timelineEventId: input.timelineEventId } : {}),
    ...(input.evidenceState ? { evidenceState: input.evidenceState } : {}),
    recoverability: {
      mode: input.riskPolicy.recoverabilityMode,
      available: recoverabilityAvailable,
      ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
      ...(input.revisionId ? { revisionId: input.revisionId } : {}),
      ...(input.undoSnapshotId ? { undoSnapshotId: input.undoSnapshotId } : {}),
    },
    ...(input.nextWork ? { nextWork: input.nextWork } : {}),
    userMessage: {
      title:
        input.userMessage?.title ??
        (input.ok ? '작업이 완료되었습니다.' : '작업을 실행할 수 없습니다.'),
      ...(input.userMessage?.description ? { description: input.userMessage.description } : {}),
    },
  };
}

export function buildCancelledExecutionResult(input: {
  actionId: string;
  objectRef: ObjectRef;
  riskPolicy: WorkbenchActionSafety;
}): WorkbenchExecutionResult {
  return buildWorkbenchExecutionResult({
    ok: false,
    handled: true,
    executionPhase: 'cancelled',
    actionId: input.actionId,
    objectRef: input.objectRef,
    riskPolicy: input.riskPolicy,
    userMessage: {
      title: '작업이 취소되었습니다.',
    },
  });
}

/** Sync claim for gated async handlers — completion arrives via pipeline / completion event. */
export function buildPendingExecutionResult(input: {
  actionId: string;
  objectRef: ObjectRef;
  riskPolicy: WorkbenchActionSafety;
  executionPhase?: Extract<WorkbenchExecutionPhase, 'pending' | 'requires_approval'>;
  userMessage?: Partial<WorkbenchExecutionResult['userMessage']>;
}): WorkbenchExecutionResult {
  return buildWorkbenchExecutionResult({
    ok: false,
    handled: true,
    executionPhase: input.executionPhase ?? 'pending',
    actionId: input.actionId,
    objectRef: input.objectRef,
    riskPolicy: input.riskPolicy,
    userMessage: input.userMessage ?? { title: '' },
  });
}
